import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { FaStar, FaMapMarkerAlt, FaCheckCircle } from "react-icons/fa";
import http from "../lib/http";
import LeadCaptureModal from "../components/LeadCaptureModal";
import ReviewModal from "../components/ReviewModal";
import './DealerDetailsPage.css'
import SEO from "../components/SEO/SEO";

const mapStyle = {
  width: "100%",
  height: "260px",
  borderRadius: "12px",
};

const renderStars = (rating = 0) =>
  Array.from({ length: 5 }, (_, i) => (
    <FaStar
      key={i}
      color={i < Math.round(rating) ? "#f5b301" : "#e4e5e9"}
      size={14}
    />
  ));

const formatDistance = (meters) => {
  if (!meters) return null;
  const km = meters / 1000;
  return km < 1 ? `${meters.toFixed(0)} m away` : `${km.toFixed(1)} km away`;
};

export default function DealerDetailsPage() {
  const { id } = useParams();

  const [dealer, setDealer] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  // ✅ NEW: contact unlock state
  const [contactUnlocked, setContactUnlocked] = useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  /* ---------------- FETCH DEALER ---------------- */
  const [inventory, setInventory] = useState([]);
  useEffect(() => {
    const fetchDealer = async () => {
      try {
        const res = await http.get(`/api/dealers/${id}`);
        setDealer(res.data);
        const inventoryRes =
        await http.get(
          `/api/inventory/dealer/${id}`
        );

      setInventory(inventoryRes.data);
      } catch (err) {
        console.error("Dealer load error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDealer();
  }, [id]);

  /* ---------------- SAFE REVIEWS ---------------- */

  const approvedReviews = useMemo(() => {
    return dealer?.reviews?.filter((r) => r.approved) || [];
  }, [dealer]);

  /*----------------- Phone unloack ---------------------*/
  useEffect(() => {
  const saved = localStorage.getItem(
    `dealer_${id}_unlocked`
  );

  if (!saved) return;

  const data = JSON.parse(saved);

  if (Date.now() < data.expiresAt) {
    setContactUnlocked(true);
  } else {
    localStorage.removeItem(
      `dealer_${id}_unlocked`
    );
  }
}, [id]);


  /* ---------------- WHATSAPP FUNCTION ---------------- */

  const sendWhatsAppNotifications = async (lead) => {
    try {
      await http.post("/api/whatsapp/send", {
        dealerPhone: dealer.contact,
        adminPhone: "917975617119", // 👉 replace with your admin number
        leadName: lead.name,
        leadPhone: lead.phone,
        dealerName: dealer.name,
      });
    } catch (err) {
      console.error("WhatsApp send failed", err);
    }
  };

  const sendWhatsAppToDealer = async (lead) => {
  try {
    await http.post("/api/whatsapp/send", {
      to: dealer.contact, // ✅ dynamic dealer number
      leadName: lead.name,
      leadPhone: lead.phone,
      dealerName: dealer.name,
    });
  } catch (err) {
    console.error("WhatsApp send failed", err);
  }
};
  /* ---------------- UI STATES ---------------- */

  if (loading) {
    return <p className="text-center mt-5">Loading dealer details…</p>;
  }

  if (!dealer) {
    return <p className="text-center mt-5">Dealer not found</p>;
  }

  const coords = dealer.location?.coordinates;

  /* ---------------- PAGE ---------------- */

  return (
    <>
    <SEO
    title={`${dealer.name} | ${dealer.category} Dealer in ${dealer.locationName} | BuildNear`}
    description={`${dealer.name} is a verified ${dealer.category} dealer located in ${dealer.locationName}. View contact details, ratings, reviews and directions.`}
    keywords={`${dealer.category}, ${dealer.locationName}, construction dealer, BuildNear`}
    url={`https://buildnear.in/dealers/${dealer.slug || dealer._id}`}
/>

    <div className="container my-4">
      <div className="dealer-hero">
        <img
          src={dealer.image || "/images/dealer-placeholder.jpg"}
          alt={dealer.name}
          className="dealer-banner"
        />

        <div className="dealer-hero-content">

          <h2>
            {dealer.name}
            <FaCheckCircle className="ms-2 text-success" />
          </h2>

          <p className="text-muted">
            {dealer.locationName || dealer.address}
          </p>

        </div>
      </div>

    {/* CATEGORY */}
            {dealer.category && (
              <span className="badge bg-secondary mb-2">
                {dealer.category}
              </span>
            )}
      <div className="row g-4">

        {/* LEFT SECTION */}
        <div className="col-lg-8">
          <div className="card shadow-sm p-4">

            

            {/* DISTANCE */}
            {dealer.distance && (
              <p className="small text-muted">
                📍 {formatDistance(dealer.distance)}
              </p>
            )}

            {/* RATING */}
            {!!dealer.rating && (
              <div className="mb-3">
                {renderStars(dealer.rating)}
                <span className="ms-2 small text-muted">
                  ({approvedReviews.length} reviews)
                </span>
              </div>
            )}

            {/* MAP */}
            {coords && (
              <div className="mt-3">
                {isLoaded ? (
                  <GoogleMap
                    mapContainerStyle={mapStyle}
                    center={{ lat: coords[1], lng: coords[0] }}
                    zoom={14}
                  >
                    <Marker position={{ lat: coords[1], lng: coords[0] }} />
                  </GoogleMap>
                ) : (
                  <div
                    style={{
                      ...mapStyle,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#f3f3f3",
                    }}
                  >
                    Loading map…
                  </div>
                )}
              </div>
            )}



            {/* INVENTORY */}

          {inventory.length > 0 && (

            <div className="mt-4">

              <div className="d-flex justify-content-between align-items-center mb-3">

                <h5 className="mb-0">
                  Available Products
                </h5>

                <span className="badge bg-success">
                  {inventory.length} Products In Stock
                </span>

              </div>


            <div className="row g-3">

            {inventory.map(item => (

            <div className="col-md-6" key={item._id}>

            <div className="inventory-card">

            <h6>{item.productName}</h6>

            <div>{item.brand}</div>

            <div>
            ₹{item.price}
            </div>

            <span>
            {item.stockQty} {item.unit}
            </span>

            </div>

            </div>

            ))}

            </div>


        </div>

      )}

            {/* ABOUT */}
            <div className="mt-4">
              <h5>About Dealer</h5>
              <div className="dealer-stats">

                  <div className="stat-box">
                     <small>Category:</small>
                      <strong>{dealer.category}</strong>
                  </div>

                  <div className="stat-box">
                    <small>Status:</small>
                     <strong>
                      Verified Dealer
                    </strong>
                  </div>

                  <div className="stat-box">
                    <small>Joined:</small>
                    <strong>
                      {new Date(dealer.joinedDate).toLocaleDateString()}
                    </strong>
                  </div>

                  <div className="stat-box">
                    <small>Rating:</small>
                    <strong>
                      {dealer.rating?.toFixed(1) || 0}/5
                    </strong>
                  </div>

                </div>
              <p className="text-muted">
                Trusted supplier in <strong>{dealer.category}</strong> with
                verified credentials and local market experience.
              </p>
            </div>

            {/* REVIEWS */}
            <div className="review-summary">

              <h3>
              ⭐ {dealer.rating?.toFixed(1)}
              </h3>

              <p>
              Based on {approvedReviews.length} reviews
              </p>

              </div>
            {!!approvedReviews.length && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Customer Reviews</h5>

                  <span className="badge bg-success">
                    {approvedReviews.length} Reviews
                  </span>
                </div>

                {approvedReviews.slice(0, 5).map((r, i) => (
                  <div
                    key={i}
                    className="border rounded p-3 mb-3"
                  >
                    <div>
                      {renderStars(r.rating)}
                    </div>

                    <p className="mt-2 mb-1">
                      {r.comment}
                    </p>

                    <small className="text-muted">
                      — {r.name}
                      {r.createdAt &&
                        ` • ${new Date(
                          r.createdAt
                        ).toLocaleDateString()}`
                      }
                    </small>
                  </div>
                ))}

              </div>
            )}
            
          </div>
        </div>

        {/* RIGHT SECTION */}
        <div className="col-lg-4">
          <div
            className="card shadow-sm p-4 sticky-top"
            style={{ top: 90 }}
          >
            <h5 className="fw-bold mb-2">Get Best Price</h5>
             {dealer.etaHours && (
              <div className="alert alert-info py-2">
                🚚 Delivery ETA:
                {" "}
                {dealer.etaHours < 1
                  ? `${Math.round(dealer.etaHours * 60)} mins`
                  : `${dealer.etaHours.toFixed(1)} hrs`}
              </div>
            )} 
            <p className="text-muted small">
              Share your requirement & receive a quick callback from the dealer.
            </p>
            <div className="trust-box">

            <div>✓ Verified Dealer</div>

            <div>⚡ Quick Response</div>

            <div>🔒 Contact Protected</div>

            </div>
            {/* ✅ UPDATED CONTACT BLOCK */}
            <div className="border rounded p-3 text-center mb-3 bg-light">
              {contactUnlocked ? (
                <>
                  <div className="fw-bold fs-5 text-success">
                    📞 {dealer.contact}
                  </div>

                  <small className="text-success">
                    Contact Unlocked ✔
                  </small>

                  <div className="mt-2">
                    <a
                      href={`tel:${dealer.contact}`}
                      className="btn btn-primary btn-sm me-2"
                    >
                      Call
                    </a>

                    <a
                      href={`https://wa.me/91${dealer.contact}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-success btn-sm"
                    >
                      WhatsApp
                    </a>
                  </div>
                </>
              ) : (
                <>
                  <div className="fw-bold fs-5">
                    📞 XXXXX XXXXX
                  </div>

                  <small className="text-muted">
                    Submit enquiry to reveal contact details
                  </small>
                </>
              )}
            </div>

            {/* CTA */}
            <button
              className="btn btn-primary w-100 mb-2"
              onClick={() => setShowLeadModal(true)}
            >
              Contact Dealer
            </button>

            <button
              className="btn btn-outline-secondary w-100"
              onClick={() => setShowReviewModal(true)}
            >
              Add Review
            </button>

            <p className="text-muted small text-center mb-0 mt-2">
              100% free · No spam · Quick response
            </p>
          </div>
        </div>

      </div>

      {/* LEAD MODAL */}
      <LeadCaptureModal
        show={showLeadModal}
        dealer={dealer}
        category={dealer.category}
        onClose={() => setShowLeadModal(false)}
        onSuccess={async (leadData) => {
          setShowLeadModal(false);

          setContactUnlocked(true);

          localStorage.setItem(
            `dealer_${dealer._id}_unlocked`,
            JSON.stringify({
              unlocked: true,
              expiresAt: Date.now() + 24 * 60 * 60 * 1000
            })
          );

          await sendWhatsAppNotifications(leadData);
          await sendWhatsAppToDealer(leadData);
        }}
      />

      {/* REVIEW MODAL */}
      <ReviewModal
        show={showReviewModal}
        dealerId={dealer._id}
        onClose={() => setShowReviewModal(false)}
        onSuccess={async () => {

          setShowReviewModal(false);

          alert(
            "Review submitted successfully. It will be visible after admin approval."
          );

          const res = await http.get(`/api/dealers/${id}`);
          setDealer(res.data);
        }}
      />
    </div>
        </>
  );
}